import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  MessageCircle,
  CheckCircle,
  User
} from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * BasicProfileView - Simplified profile view for users who don't have
 * Admin/Manager permissions viewing another user's profile.
 *
 * Shows only:
 * - Avatar, name, job title, department
 * - Email and phone (if public)
 * - Compliance status badge
 * - Shared spaces
 * - Contact button
 */
export function BasicProfileView({
  userProfile,
  company,
  qualificationStats,
  sharedSpaces = [],
  onContact
}) {
  if (!userProfile) return null;

  const displayName = userProfile.display_name ||
    `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() ||
    'Unknown User';

  const initials = `${userProfile.first_name?.[0] || ''}${userProfile.last_name?.[0] || ''}`.toUpperCase() || '??';

  // Compliance badge color based on percentage
  const getComplianceBadge = () => {
    const percentage = qualificationStats?.compliancePercentage || 0;
    if (percentage >= 90) {
      return { color: 'bg-green-100 text-green-700 border-green-200', label: 'Compliant' };
    } else if (percentage >= 70) {
      return { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Partially Compliant' };
    } else {
      return { color: 'bg-red-100 text-red-700 border-red-200', label: 'Needs Attention' };
    }
  };

  const complianceBadge = getComplianceBadge();

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="overflow-hidden">
          {/* Header with gradient background */}
          <div className="h-24 bg-gradient-to-r from-blue-500 to-indigo-600" />

          <CardContent className="pt-0 pb-6">
            {/* Avatar - overlapping the header */}
            <div className="flex flex-col items-center -mt-12">
              <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                <AvatarImage src={userProfile.avatar_url} alt={displayName} />
                <AvatarFallback className="text-2xl font-semibold bg-gray-100">
                  {initials}
                </AvatarFallback>
              </Avatar>

              {/* Name and role */}
              <h1 className="text-xl font-bold text-gray-900 mt-4">{displayName}</h1>

              {userProfile.job_title && (
                <div className="flex items-center gap-1.5 text-gray-600 mt-1">
                  <Briefcase className="h-4 w-4" />
                  <span>{userProfile.job_title}</span>
                </div>
              )}

              {userProfile.department && (
                <div className="flex items-center gap-1.5 text-gray-500 mt-1">
                  <Building2 className="h-4 w-4" />
                  <span>{userProfile.department}</span>
                </div>
              )}

              {/* Compliance Badge */}
              {qualificationStats?.total > 0 && (
                <Badge
                  variant="outline"
                  className={`mt-3 ${complianceBadge.color}`}
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {complianceBadge.label} ({qualificationStats.compliancePercentage}%)
                </Badge>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100 my-6" />

            {/* Contact Info */}
            <div className="space-y-3">
              {userProfile.email && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <a
                    href={`mailto:${userProfile.email}`}
                    className="text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    {userProfile.email}
                  </a>
                </div>
              )}

              {userProfile.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-4 w-4 text-green-600" />
                  </div>
                  <a
                    href={`tel:${userProfile.phone}`}
                    className="text-gray-700 hover:text-green-600 transition-colors"
                  >
                    {userProfile.phone}
                  </a>
                </div>
              )}

              {userProfile.location && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-gray-700">{userProfile.location}</span>
                </div>
              )}
            </div>

            {/* Shared Spaces */}
            {sharedSpaces.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-6" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Shared Spaces ({sharedSpaces.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {sharedSpaces.slice(0, 5).map((space) => (
                      <Badge
                        key={space.id}
                        variant="secondary"
                        className="bg-gray-100 text-gray-700"
                      >
                        {space.name}
                      </Badge>
                    ))}
                    {sharedSpaces.length > 5 && (
                      <Badge variant="secondary" className="bg-gray-100 text-gray-500">
                        +{sharedSpaces.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Contact Button */}
            {userProfile.email && (
              <>
                <div className="border-t border-gray-100 my-6" />
                <div className="flex justify-center">
                  <Button
                    onClick={() => window.location.href = `mailto:${userProfile.email}`}
                    className="gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Send Message
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info note */}
        <p className="text-center text-xs text-gray-400 mt-4">
          Viewing limited profile information. Contact an administrator for more details.
        </p>
      </motion.div>
    </div>
  );
}

export default BasicProfileView;
